import { cn } from '@/lib/utils'
import { formatCurrencyWhole } from '@/lib/currency-utils'
import { JobBoardStageEnum, JobPhaseEnum } from '@/gen/cortado/v1/settings_pb'
import type { GetPipelineSummaryResponse } from '@/gen/cortado/v1/jobs_pb'
import { PIPELINE_TILE_LABEL, pipelineStageBucket, type PipelineTileKey } from '@/components/pipeline/pipeline-job-view'

interface PipelinePhaseTilesProps {
  buckets: GetPipelineSummaryResponse['buckets']
  stageBuckets: GetPipelineSummaryResponse['stageBuckets']
  balanceDueCents: bigint
  openInvoiceCount: number
  overdueInvoiceCount: number
  activeTile: PipelineTileKey
  onSelect: (tile: PipelineTileKey) => void
  isLoading?: boolean
}

const TILE_ORDER: PipelineTileKey[] = ['all', 'lead', 'quote', 'booking', 'due']

export function PipelinePhaseTiles({
  buckets,
  stageBuckets,
  balanceDueCents,
  openInvoiceCount,
  overdueInvoiceCount,
  activeTile,
  onSelect,
  isLoading,
}: PipelinePhaseTilesProps) {
  const byPhase = new Map(buckets.map((bucket) => [bucket.bucket, bucket]))
  const leadCount = byPhase.get(JobPhaseEnum.JOB_PHASE_NEW)?.count ?? 0
  const quoteBucket = byPhase.get(JobPhaseEnum.JOB_PHASE_QUOTING)
  const bookedBucket = byPhase.get(JobPhaseEnum.JOB_PHASE_BOOKED)
  const completeBucket = byPhase.get(JobPhaseEnum.JOB_PHASE_COMPLETE)
  const bookingCount = (bookedBucket?.count ?? 0) + (completeBucket?.count ?? 0)
  const bookingQuotedCents = (bookedBucket?.quotedCents ?? 0n) + (completeBucket?.quotedCents ?? 0n)
  const totalCount = buckets.reduce((sum, bucket) => sum + bucket.count, 0)

  const leadNew = pipelineStageBucket(stageBuckets, JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW)?.count ?? 0
  const leadInProgress = pipelineStageBucket(stageBuckets, JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_IN_PROGRESS)?.count ?? 0
  const quoteSentCents = pipelineStageBucket(stageBuckets, JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_SENT)?.quotedCents ?? 0n
  const quoteDeclinedCount =
    pipelineStageBucket(stageBuckets, JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_DECLINED)?.count ?? 0
  const pendingDepositCount =
    pipelineStageBucket(stageBuckets, JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_PENDING_DEPOSIT)?.count ?? 0
  const confirmedCount = pipelineStageBucket(stageBuckets, JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_CONFIRMED)?.count ?? 0

  return (
    <div className="grid flex-none grid-cols-5 overflow-hidden rounded-xl border border-border bg-card">
      {TILE_ORDER.map((tile) => {
        const isActive = tile === activeTile
        const { bigNumber, sub } = tileContent(tile, {
          isLoading,
          totalCount,
          leadCount,
          leadNew,
          leadInProgress,
          quoteBucket,
          quoteSentCents,
          quoteDeclinedCount,
          bookingCount,
          bookingQuotedCents,
          pendingDepositCount,
          confirmedCount,
          balanceDueCents,
          openInvoiceCount,
          overdueInvoiceCount,
        })

        return (
          <button
            key={tile}
            type="button"
            onClick={() => onSelect(tile)}
            aria-pressed={isActive}
            className={cn(
              'min-w-0 border-l border-border px-4.5 py-3.5 text-left transition-colors first:border-l-0 hover:bg-accent/50',
              isActive && 'border-b-2 border-b-brand bg-accent',
            )}
          >
            <div
              className={cn(
                'font-financial text-xs uppercase tracking-[0.2em] text-dim',
                isActive && 'text-brand',
              )}
            >
              {PIPELINE_TILE_LABEL[tile]}
            </div>
            <div className="font-financial mt-1.5 text-2xl font-medium">{bigNumber}</div>
            <div className="truncate text-xs text-muted-foreground">{sub}</div>
          </button>
        )
      })}
    </div>
  )
}

function tileContent(
  tile: PipelineTileKey,
  ctx: {
    isLoading?: boolean
    totalCount: number
    leadCount: number
    leadNew: number
    leadInProgress: number
    quoteBucket?: { count: number; quotedCents: bigint }
    quoteSentCents: bigint
    quoteDeclinedCount: number
    bookingCount: number
    bookingQuotedCents: bigint
    pendingDepositCount: number
    confirmedCount: number
    balanceDueCents: bigint
    openInvoiceCount: number
    overdueInvoiceCount: number
  },
): { bigNumber: string; sub: string } {
  if (ctx.isLoading) return { bigNumber: '—', sub: 'Loading…' }

  switch (tile) {
    case 'all':
      return { bigNumber: String(ctx.totalCount), sub: `${ctx.totalCount} active records` }
    case 'lead':
      return {
        bigNumber: String(ctx.leadCount),
        sub: ctx.leadCount === 0 ? 'No jobs' : `${ctx.leadNew} new · ${ctx.leadInProgress} in progress`,
      }
    case 'quote': {
      const count = ctx.quoteBucket?.count ?? 0
      return {
        bigNumber: String(count),
        sub:
          count === 0
            ? 'No jobs'
            : `$${formatCurrencyWhole(Number(ctx.quoteSentCents))} out · ${ctx.quoteDeclinedCount} declined`,
      }
    }
    case 'booking':
      return {
        bigNumber: String(ctx.bookingCount),
        sub:
          ctx.bookingCount === 0
            ? 'No jobs'
            : `${ctx.pendingDepositCount} pending deposit · ${ctx.confirmedCount} confirmed`,
      }
    case 'due':
      return {
        bigNumber: `$${formatCurrencyWhole(Number(ctx.balanceDueCents))}`,
        sub: `${ctx.openInvoiceCount} invoices open · ${ctx.overdueInvoiceCount} overdue`,
      }
  }
}
